import { createSelector } from 'reselect';

import userRoles from '../../shared/authorization/userRoles';
import assignmentPermissions from '../../shared/authorization/assignmentPermissions';

import { normalizeAssignments } from '../addEditUserSettingsForm.initialState';

export const isLoadingUsersSelector = ({ users }) => users.isLoadingUsers;

export const isSavingInProgressSelector = ({ users }) => users.isSavingInProgress;

export const rolesOptionsSelector = createSelector(
  ({ users }) => users.userSettingsFormData.availableRoleAssignments,
  ({ users }) => users.userSettingsFormData.rolesToChooseFrom,
  (availableRoleAssignments, rolesToChooseFrom) => availableRoleAssignments.map(({ clientId, roles }) => ({
    clientId,
    roles: rolesToChooseFrom[clientId].map(role => ({
      ...role,
      clientId: role.clientId === -1 ? clientId : role.clientId,
      disabled: !roles.includes(role.roleId),
    })),
  }))
);

export const rolesContextSelector = createSelector(
  ({ users }) => users.userSettingsFormData.availableRoleAssignments,
  ({ users }) => users.userSettingsFormData.rolesToChooseFrom,
  ({ users }) => users.userSettingsFormData.userClientList,
  (availableRoleAssignments, rolesToChooseFrom, clientList) =>
    availableRoleAssignments.reduce((rolesContext, { clientId }) => {
      const clientName = clientList.find(client => client.clientId === clientId).name;
      rolesContext.set(clientName, {
        clientId,
        roles: rolesToChooseFrom[clientId].reduce((roles, role) => {
          roles.set(role.roleName, role.roleId);
          return roles;
        }, new Map()),
      });
      return rolesContext;
    }, new Map())
);

export const permissionsOptionsSelector = createSelector(
  ({ users }) => users.userSettingsFormData.availablePermissions,
  ({ users }) => users.userSettingsFormData.allPermissions,
  ({ users }) => users.userSettingsFormData.userClientList,
  (availablePermissions, allPermissions, userClientList) =>
    availablePermissions.map(({ clientId, availablePermissions: permissions }) => {
      const roleForClient = userClientList.find(client => client.clientId === clientId).roleName;

      if (roleForClient === userRoles.SUPER_USER || roleForClient === userRoles.POWER_USER) {
        return {
          clientId,
          availablePermissions: allPermissions,
        };
      }

      return {
        clientId,
        availablePermissions: allPermissions.map(permission => ({
          ...permission,
          disabled: !permissions.includes(permission.value),
        })),
      };
    })
);

export const settingsFormDataSelector = ({ users }) => users.userSettingsFormData;

const generateAvaialblePermissionsFoRole = (roles, availablePermissions) =>
  Object.keys(roles).reduce(
    (acc, key) => (acc.concat(roles[key])),
    []
  ).reduce((permissionsByRole, { roleName: role }) => ({
    ...permissionsByRole,
    [role]: availablePermissions.map(permission => ({
      ...permission,
      disabled: permission.disabled,
    })),
  }), {});

export const availablePermissionsForRoleSelector = createSelector(
  ({ users }) => users.userSettingsFormData.rolesToChooseFrom,
  permissionsOptionsSelector,
  (roles, permissionsOptions) => permissionsOptions.map(({ clientId, availablePermissions }) => ({
    clientId,
    availablePermissions: generateAvaialblePermissionsFoRole(roles, availablePermissions),
  }))
);

export const isAllowedToEditUsersSelector = createSelector(
  ({ users }) => users.userSettingsFormData.userClientList,
  ({ users }) => users.userSettingsFormData.availablePermissions,
  (clients, availablePermissions) =>
    !!clients.find(({ roleName }) => roleName === userRoles.SUPER_USER || roleName === userRoles.POWER_USER)
    || !!availablePermissions.find(
      client => !!client.availablePermissions.find(privilege => privilege === assignmentPermissions.UserAdmin)
    )
);

export const isUserModifiedSelector = ({ users }) => users.isUserModified;

export const userToEditSsoSelector = ({ users }) => users.userToEditSso;

export const normalizedLoggedInUserAssignmentsSelector = createSelector(
  state => state.users.loggedInUser,
  loggedInUser => loggedInUser ? normalizeAssignments(loggedInUser.clients) : []
);

export const canEditClientsSelector = createSelector(
  ({ users }) => users.loggedInUser,
  loggedInUser => loggedInUser ? loggedInUser.clients : []
);

export const clientsFormDataSelector = createSelector(
  canEditClientsSelector,
  settingsFormDataSelector,
  (canEditClients, settingsFormData) => canEditClients.map(client => ({
    ...client,
    ...settingsFormData.userClientList.find(({ clientId }) => clientId === client.clientId),
  }))
);

const getFilteredData = (clientId, formData) => {
  if (clientId === null) {
    return [];
  }

  const filteredData = formData.find(data => data.clientId === clientId);

  return filteredData
    ? filteredData.data
    : [];
};

export const filteredSettingsFormDataSelector = createSelector(
  clientId => !isNaN(clientId) ? clientId : null,
  (clientId, isSelectedClientChecked) => isSelectedClientChecked,
  (clientId, isSelectedClientChecked, userSettingsFormData) => userSettingsFormData,
  (clientId, isSelectedClientChecked, userSettingsFormData) => {
    const { businessUnits, companyCodes, connections, countries, businesses } = userSettingsFormData;

    return {
      ...userSettingsFormData,
      businessUnits: isSelectedClientChecked ? getFilteredData(clientId, businessUnits) : [],
      companyCodes: isSelectedClientChecked ? getFilteredData(clientId, companyCodes) : [],
      connections: isSelectedClientChecked ? getFilteredData(clientId, connections) : [],
      countries: isSelectedClientChecked ? getFilteredData(clientId, countries) : [],
      businesses: isSelectedClientChecked ? getFilteredData(clientId, businesses) : [],
    };
  }
);

export const currentUserClientSelector = createSelector(
  ({ user }) => user.user,
  ({ user }) => user.clients,
  (user, clients) => {
    const client = clients.find(client => client.clientId === user.lastClientId);

    return {
      clientId: user.lastClientId,
      name: client && client.name,
      role: user.role,
    };
  }
);

export const isUserReadOnlySelector = ({ users }) => users.readOnly;

export const isEmailUniqueSelector = ({ users }) => users.isEmailUnique;

export const isPwcEmailSetupSelector = ({ users }) => users.isPwcEmailSetup;

export const isUserEmailUnderDomainSelector = ({ users }) => users.isUserEmailUnderDomain;

export const isSSOUniqueSelector = ({ users }) => users.isSSOUnique;

export const isCheckingIfUsersSSOExistsSelector = ({ users }) => users.isCheckingIfUsersSSOExists;

export const isCheckingIfUsersEmailExistsSelector = ({ users }) => users.isCheckingIfUsersEmailExists;

export const isCheckingIfUserEmailIsUnderDomainSelector = ({ users }) => users.isCheckingIfUserEmailIsUnderDomain;

export const userToEditSelector = state => state.users.userToEdit;
export const openForEditSelector = state => state.users.openFormForEdit;

const loggedInUserSelector = ({ users }) => users.loggedInUser;

const clientsAllowedToBeAssignedSelector = createSelector(
  loggedInUserSelector,
  loggedInUser => loggedInUser && loggedInUser.clients
    ? loggedInUser.clients.filter(({ userPermissions, roleName }) => roleName === userRoles.SUPER_USER
      || roleName === userRoles.POWER_USER
      || userPermissions.some(({ privilegeCode }) => privilegeCode === assignmentPermissions.UserAdmin)
    )
    : []
);

export const lastClientOptionsSelector = createSelector(
  clientsAllowedToBeAssignedSelector,
  userToEditSelector,
  (allowedClients, userToEdit) => userToEdit && userToEdit.clients
    ? userToEdit.clients
      .filter(client => allowedClients.some(({ clientId }) => clientId === client.clientId))
      .map(({ name, clientId }) => ({ label: name, value: clientId }))
    : []
);

export const canHaveLastClientSetSelector = createSelector(
  lastClientOptionsSelector,
  lastClientOptions => lastClientOptions.length > 0
);

export const rolesAssignmentsMapsSelector = state => state.users.rolesAssignmentsMaps;

export const isLoadingRolesAssignmentsMapSelector = state => Boolean(state.users.loadingRolesAssignmentsCount);

export const isFetchingInitialDataForUserSettingsFormSelector = ({ users }) =>
  Boolean(users.isFetchingInitialDataForUserSettingsForm);

export const isUserExpirationDateSelector = ({ users }) => users.isExpirationDatePrivilege;
